import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useRestaurant } from '../../context/RestaurantContext';
import { useGoogleMaps } from '../../hooks/useGoogleMaps';
import { toast } from 'sonner';
import { 
  MapPin, RefreshCw, Car, Bike, User, Clock, Package, 
  ChevronRight, Check, X, Truck
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

export default function DriverMap() {
  const { api } = useAuth();
  const { selectedRestaurant } = useRestaurant();
  const { loaded: googleLoaded } = useGoogleMaps();
  const [drivers, setDrivers] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [activeTab, setActiveTab] = useState('drivers'); // 'drivers' or 'orders'
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const markersRef = useRef([]);
  const restaurantMarkerRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [driversRes, ordersRes] = await Promise.all([
        api.get('/drivers/nearby'),
        api.get('/orders/pending-assignment')
      ]);
      setDrivers(driversRes.data.drivers);
      setPendingOrders(ordersRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
    
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchData, 30000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [fetchData, autoRefresh]);

  // Get restaurant coordinates or fallback to default
  const getRestaurantCenter = useCallback(() => {
    console.log('Getting restaurant center:', selectedRestaurant?.latitude, selectedRestaurant?.longitude);
    if (selectedRestaurant?.latitude && selectedRestaurant?.longitude) {
      return { 
        lat: parseFloat(selectedRestaurant.latitude), 
        lng: parseFloat(selectedRestaurant.longitude) 
      };
    }
    // Default to London if no restaurant coordinates
    console.log('No restaurant coordinates, defaulting to London');
    return { lat: 51.5074, lng: -0.1278 };
  }, [selectedRestaurant]);

  // Initialize Google Map
  useEffect(() => {
    const initMap = () => {
      if (!window.google || !mapRef.current || googleMapRef.current) return;
      
      const center = getRestaurantCenter();
      
      googleMapRef.current = new window.google.maps.Map(mapRef.current, {
        center: center,
        zoom: 14,
        styles: [
          { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }
        ],
        mapTypeControl: false,
        fullscreenControl: false,
        streetViewControl: false
      });

      // Add restaurant marker
      if (selectedRestaurant?.latitude && selectedRestaurant?.longitude) {
        restaurantMarkerRef.current = new window.google.maps.Marker({
          position: center,
          map: googleMapRef.current,
          title: selectedRestaurant.name || 'Your Restaurant',
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 14,
            fillColor: '#f97316',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 3
          },
          zIndex: 1000
        });
      }
    };

    // Use the centralized hook - only init when google is loaded
    if (googleLoaded && window.google?.maps) {
      initMap();
    }

    return () => {
      if (restaurantMarkerRef.current) {
        restaurantMarkerRef.current.setMap(null);
      }
    };
  }, [selectedRestaurant, getRestaurantCenter, googleLoaded]);

  // Helper to validate coordinates
  const isValidCoordinate = (lat, lng) => {
    return typeof lat === 'number' && typeof lng === 'number' && 
           !isNaN(lat) && !isNaN(lng) &&
           lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
  };

  // Update markers
  useEffect(() => {
    if (!googleMapRef.current || !window.google) return;

    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    const validDriverMarkers = [];

    drivers.forEach(driver => {
      if (driver.current_location) {
        // Handle both old format (lat/lng) and new format (latitude/longitude)
        const lat = driver.current_location.latitude || driver.current_location.lat;
        const lng = driver.current_location.longitude || driver.current_location.lng;
        
        // Skip if coordinates are invalid
        if (!isValidCoordinate(lat, lng)) {
          console.warn(`Invalid coordinates for driver ${driver.name}:`, lat, lng);
          return;
        }
        
        const isSelected = selectedDriver?.id === driver.id;
        const marker = new window.google.maps.Marker({
          position: { lat, lng },
          map: googleMapRef.current,
          title: driver.name,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: isSelected ? 16 : 12,
            fillColor: driver.is_available ? '#22c55e' : '#94a3b8',
            fillOpacity: 1,
            strokeColor: isSelected ? '#f97316' : '#ffffff',
            strokeWeight: isSelected ? 4 : 3
          }
        });

        marker.addListener('click', () => setSelectedDriver(driver));
        markersRef.current.push(marker);
        validDriverMarkers.push(marker);
      }
    });

    // Fit bounds to show drivers and restaurant
    if (validDriverMarkers.length > 0 || (selectedRestaurant?.latitude && selectedRestaurant?.longitude)) {
      const bounds = new window.google.maps.LatLngBounds();
      
      // Include restaurant location
      if (selectedRestaurant?.latitude && selectedRestaurant?.longitude) {
        bounds.extend({ 
          lat: parseFloat(selectedRestaurant.latitude), 
          lng: parseFloat(selectedRestaurant.longitude) 
        });
      }
      
      // Include driver locations
      validDriverMarkers.forEach(marker => bounds.extend(marker.getPosition()));
      
      if (validDriverMarkers.length > 0) {
        googleMapRef.current.fitBounds(bounds);
        
        const listener = googleMapRef.current.addListener('idle', () => {
          if (googleMapRef.current.getZoom() > 15) {
            googleMapRef.current.setZoom(15);
          }
          window.google.maps.event.removeListener(listener);
        });
      }
    }
  }, [drivers, selectedDriver, selectedRestaurant]);

  const handleAssignDriver = async () => {
    if (!selectedDriver || !selectedOrder) {
      toast.error('Please select both an order and a driver');
      return;
    }

    setAssigning(true);
    try {
      await api.put(`/orders/${selectedOrder.id}/assign-driver`, {
        driver_id: selectedDriver.id
      });
      
      toast.success(`Driver ${selectedDriver.name} assigned to order #${selectedOrder.id.slice(-6)}`);
      
      // Refresh data
      await fetchData();
      setSelectedOrder(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign driver');
    } finally {
      setAssigning(false);
    }
  };

  const getVehicleIcon = (type) => {
    switch (type) {
      case 'car': return Car;
      case 'bike':
      case 'scooter': return Bike;
      default: return Car;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-blue-100 text-blue-700';
      case 'preparing': return 'bg-yellow-100 text-yellow-700';
      case 'ready': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Driver Map & Order Assignment</h1>
          <p className="text-slate-500">Assign available drivers to pending orders</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-slate-300"
            />
            Auto-refresh
          </label>
          <Button onClick={fetchData} variant="outline" size="sm" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Assignment Bar */}
      {(selectedDriver || selectedOrder) && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              {/* Selected Order */}
              <div className="flex items-center gap-2">
                <Package className={`w-5 h-5 ${selectedOrder ? 'text-orange-600' : 'text-slate-300'}`} />
                {selectedOrder ? (
                  <div>
                    <p className="font-medium text-slate-900">Order #{selectedOrder.id.slice(-6)}</p>
                    <p className="text-xs text-slate-500">{selectedOrder.restaurant_name}</p>
                  </div>
                ) : (
                  <span className="text-slate-400">Select an order</span>
                )}
              </div>
              
              <ChevronRight className="w-5 h-5 text-slate-300" />
              
              {/* Selected Driver */}
              <div className="flex items-center gap-2">
                <Truck className={`w-5 h-5 ${selectedDriver ? 'text-orange-600' : 'text-slate-300'}`} />
                {selectedDriver ? (
                  <div>
                    <p className="font-medium text-slate-900">{selectedDriver.name}</p>
                    <p className="text-xs text-slate-500 capitalize">{selectedDriver.vehicle_type}</p>
                  </div>
                ) : (
                  <span className="text-slate-400">Select a driver</span>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setSelectedDriver(null); setSelectedOrder(null); }}
              >
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
              <Button
                onClick={handleAssignDriver}
                disabled={!selectedDriver || !selectedOrder || assigning}
                className="btn-primary"
              >
                {assigning ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Assign Driver
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Map */}
        <div className="lg:col-span-2">
          <Card className="h-[500px] overflow-hidden">
            <div ref={mapRef} className="w-full h-full bg-slate-100">
              {!process.env.REACT_APP_GOOGLE_MAPS_API_KEY && (
                <div className="flex items-center justify-center h-full text-slate-500">
                  <div className="text-center">
                    <MapPin className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p className="font-medium">Google Maps API key required</p>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Side Panel with Tabs */}
        <div className="space-y-4">
          {/* Tab Buttons */}
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('orders')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                activeTab === 'orders' ? 'bg-white shadow text-slate-900' : 'text-slate-600'
              }`}
            >
              <Package className="w-4 h-4" />
              Orders
              {pendingOrders.length > 0 && (
                <span className="bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {pendingOrders.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('drivers')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                activeTab === 'drivers' ? 'bg-white shadow text-slate-900' : 'text-slate-600'
              }`}
            >
              <Car className="w-4 h-4" />
              Drivers
              <span className="bg-green-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                {drivers.filter(d => d.is_available).length}
              </span>
            </button>
          </div>

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Pending Orders</CardTitle>
              </CardHeader>
              <CardContent className="max-h-[400px] overflow-y-auto">
                {loading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="h-20 bg-slate-100 rounded-lg animate-pulse" />
                    ))}
                  </div>
                ) : pendingOrders.length > 0 ? (
                  <div className="space-y-2">
                    {pendingOrders.map(order => (
                      <div
                        key={order.id}
                        onClick={() => setSelectedOrder(order)}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          selectedOrder?.id === order.id
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-medium text-slate-900">#{order.id.slice(-6)}</p>
                            <p className="text-sm text-slate-500">{order.restaurant_name}</p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(order.status)}`}>
                            {order.status}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <span>£{order.total?.toFixed(2)}</span>
                          <span>{new Date(order.created_at).toLocaleTimeString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Check className="w-12 h-12 mx-auto text-green-300 mb-3" />
                    <p className="text-slate-500">All orders have drivers assigned!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Drivers Tab */}
          {activeTab === 'drivers' && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Available Drivers</CardTitle>
              </CardHeader>
              <CardContent className="max-h-[400px] overflow-y-auto">
                {loading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
                    ))}
                  </div>
                ) : drivers.length > 0 ? (
                  <div className="space-y-2">
                    {drivers.map(driver => {
                      const VehicleIcon = getVehicleIcon(driver.vehicle_type);
                      const hasValidLocation = driver.current_location && 
                        isValidCoordinate(driver.current_location.latitude, driver.current_location.longitude);
                      return (
                        <div
                          key={driver.id}
                          onClick={() => setSelectedDriver(driver)}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            selectedDriver?.id === driver.id
                              ? 'border-orange-500 bg-orange-50'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              driver.is_available ? 'bg-green-100' : 'bg-slate-100'
                            }`}>
                              <VehicleIcon className={`w-5 h-5 ${
                                driver.is_available ? 'text-green-600' : 'text-slate-400'
                              }`} />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-slate-900">{driver.name}</p>
                              <p className="text-xs text-slate-500">
                                {driver.completed_deliveries || 0} deliveries
                                {!hasValidLocation && (
                                  <span className="text-amber-600 ml-1">(no GPS)</span>
                                )}
                              </p>
                            </div>
                            <div className={`w-3 h-3 rounded-full ${
                              driver.is_available ? 'bg-green-500' : 'bg-slate-300'
                            }`} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <User className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">No drivers available</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Selected Driver Details */}
          {selectedDriver && activeTab === 'drivers' && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Driver Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                      <User className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{selectedDriver.name}</p>
                      <p className="text-sm text-slate-500">{selectedDriver.phone || 'No phone'}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 pt-2">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500">Vehicle</p>
                      <p className="font-medium text-slate-800 capitalize">
                        {selectedDriver.vehicle_type || 'Car'}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500">Deliveries</p>
                      <p className="font-medium text-slate-800">
                        {selectedDriver.completed_deliveries || 0}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-sm">
                    <div className={`w-2 h-2 rounded-full ${
                      selectedDriver.is_available ? 'bg-green-500' : 'bg-slate-300'
                    }`} />
                    <span className={selectedDriver.is_available ? 'text-green-600' : 'text-slate-500'}>
                      {selectedDriver.is_available ? 'Available' : 'Unavailable'}
                    </span>
                  </div>

                  {selectedDriver.current_location && (
                    <div className="text-xs text-slate-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Last updated: {new Date(selectedDriver.current_location.updated_at).toLocaleTimeString()}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
